export const NAME_MIN = 2;
export const NAME_MAX = 200;
export const MAX_SUB_TREATMENTS = 200;
export const NAME_PATTERN = /^[a-zA-Z0-9\s/\\&\-(),.'+]+$/;

const NAME_CHARS_HINT = "Only letters, numbers, spaces and - / & ( ) , . ' + are allowed";

const validateName = (label, value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return `${label} is required`;
  if (trimmed.length < NAME_MIN) return `${label} must be at least ${NAME_MIN} characters`;
  if (trimmed.length > NAME_MAX) return `${label} must be at most ${NAME_MAX} characters`;
  if (!NAME_PATTERN.test(trimmed)) return `${label}: ${NAME_CHARS_HINT}`;
  return "";
};

const isValidOrder = (value) => {
  if (value === "" || value === null || value === undefined) return true;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0;
};

const toOrder = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

let rowKeySeed = 0;
/** A stable React key for an editor row (saved rows use their _id). */
export const nextRowKey = () => `new-${(rowKeySeed += 1)}`;

export const emptySubTreatmentRow = (displayOrder = 1) => ({
  key: nextRowKey(),
  _id: undefined,
  name: "",
  isActive: true,
  displayOrder,
});

/** Editor rows from a stored treatment (saved rows keep their _id). */
export const subTreatmentRowsOf = (treatment) => {
  const list = Array.isArray(treatment?.subTreatments) ? treatment.subTreatments : [];
  if (!list.length) return [emptySubTreatmentRow(1)];
  return list.map((sub) => ({
    key: sub._id || nextRowKey(),
    _id: sub._id,
    name: sub.name || "",
    isActive: sub.isActive !== false,
    displayOrder: Number(sub.displayOrder) || 0,
  }));
};

/** Case-insensitive duplicate names, first spelling reported. */
export const duplicateSubTreatmentNames = (rows) => {
  const firstSpelling = new Map();
  const duplicates = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const name = String(row?.name ?? "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (firstSpelling.has(key)) {
      const first = firstSpelling.get(key);
      if (!duplicates.includes(first)) duplicates.push(first);
      return;
    }
    firstSpelling.set(key, name);
  });
  return duplicates;
};

export const validateTreatmentForm = ({
  speciality,
  name,
  displayOrder,
  subTreatments = [],
}) => {
  if (!speciality) return "Speciality is required";

  const nameError = validateName("Treatment name", name);
  if (nameError) return nameError;

  if (!isValidOrder(displayOrder)) return "Display order must be a whole number (0 or more)";

  const rows = (Array.isArray(subTreatments) ? subTreatments : []).filter(
    (row) => row?._id || String(row?.name ?? "").trim()
  );
  if (!rows.length) return "Add at least one sub-treatment";
  if (rows.length > MAX_SUB_TREATMENTS) {
    return `A treatment can have at most ${MAX_SUB_TREATMENTS} sub-treatments`;
  }

  for (const row of rows) {
    const error = validateName("Sub-treatment name", row.name);
    if (error) return error;
    if (!isValidOrder(row.displayOrder)) {
      return `Display order of "${String(row.name).trim()}" must be a whole number (0 or more)`;
    }
  }

  const duplicates = duplicateSubTreatmentNames(rows);
  if (duplicates.length) return `Duplicate sub-treatment: ${duplicates.join(", ")}`;

  return "";
};

export const buildTreatmentPayload = ({
  speciality,
  name,
  isActive = true,
  displayOrder,
  subTreatments = [],
}) => ({
  speciality: typeof speciality === "string" ? speciality : speciality?._id,
  name: String(name ?? "").trim(),
  isActive: Boolean(isActive),
  displayOrder: toOrder(displayOrder),
  subTreatments: (Array.isArray(subTreatments) ? subTreatments : [])
    .filter((row) => row?._id || String(row?.name ?? "").trim())
    .map((row) => ({
      ...(row._id ? { _id: row._id } : {}),
      name: String(row.name ?? "").trim(),
      isActive: row.isActive !== false,
      displayOrder: toOrder(row.displayOrder),
    })),
});

export const removeSubTreatmentRow = (rows, key) =>
  (Array.isArray(rows) ? rows : []).flatMap((row) => {
    if (row.key !== key) return [row];
    return row._id ? [{ ...row, isActive: false }] : [];
  });

export const importFailures = (data = {}) =>
  (Array.isArray(data?.results) ? data.results : [])
    .filter((item) => item?.status === "failed")
    .map((item) => ({
      row: Number(item.row) || null,
      label: [item.speciality, item.treatment, item.subTreatment]
        .map((part) => String(part ?? "").trim())
        .filter(Boolean)
        .join(" › "),
      reason: String(item.message ?? "Could not import this row"),
    }))
    .sort((a, b) => (a.row ?? Infinity) - (b.row ?? Infinity));

export const importSummary = (data = {}) => {
  const { created = 0, updated = 0, skipped = 0, failed = 0 } = data || {};
  return {
    level: failed > 0 ? "warning" : "success",
    message: `Import complete — ${created} created, ${updated} updated, ${skipped} skipped, ${failed} failed.`,
  };
};
